import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import usePageTracking from '../hooks/usePageTracking';
import Header from './Header';

const Layout = ({ children }) => {
    const { logout, user } = useAuth();
    const { config } = useConfig();
    const navigate = useNavigate();
    const location = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    usePageTracking();

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const isStaff = user?.role === 'staff' || user?.role === 'admin';

    const getPageKey = () => {
        const path = location.pathname;
        if (path === '/' || path === '') return 'home';
        if (path.includes('agenda')) return 'agenda';
        if (path.includes('speakers')) return 'speakers';
        if (path.includes('streaming')) return 'streaming';
        if (path.includes('gallery')) return 'gallery';
        if (path.includes('profile')) return 'profile';
        if (path.includes('login')) return 'login';
        if (path.includes('register')) return 'register';
        if (path.includes('welcome')) return 'welcome';
        if (path.includes('thanks')) return 'thanks';
        if (path.includes('certificate')) return 'certificate';
        if (path.includes('questions-expert')) return 'questions_private';
        if (path.includes('interactiva-stats')) return 'interactiva-stats';
        return 'home';
    };

    const pageKey = getPageKey();
    const pageDesign = config?.pageDesigns?.[pageKey] || { enabled: true, bgImage: '', showBrain: true };

    const navItems = isStaff
        ? [
            { path: '/', label: 'INICIO', icon: '🏠' },
            { path: '/agenda', label: 'AGENDA', icon: '📅' },
            { path: '/scanner', label: 'ESCANEAR', icon: '📷' },
            { path: '/questions-expert', label: 'PREGUNTAR', icon: '💬' },
            { path: '/interactiva-gestion', label: 'GESTIÓN TRIVIA', icon: '⚙️' },
            { path: '/interactiva-stats/0', label: 'MÉTRICAS TRIVIA', icon: '📊' },
            { path: '/staff-gallery', label: 'CARGAR FOTOS', icon: '📸' },
            { path: '/profile', label: 'PERFIL', icon: '👤' },
        ]
        : [
            { path: '/', label: 'INICIO', icon: '🏠', key: 'home' },
            { path: '/agenda', label: 'AGENDA', icon: '📅', key: 'agenda' },
            { path: '/welcome', label: 'BIENVENIDA', icon: '📜', key: 'welcome' },
            { path: '/speakers', label: 'CONFERENCISTAS', icon: '🎙️', key: 'speakers' },
            { path: '/streaming', label: 'EN VIVO', icon: '📺', key: 'streaming' },
            { path: '/questions-expert', label: 'PREGUNTAR', icon: '💬', key: 'questions_private' },
            { 
                path: '/event-gallery', 
                label: 'GALERÍA', 
                icon: '🖼️', 
                key: 'gallery',
                type: config?.pageDesigns?.gallery?.type,
                url: config?.pageDesigns?.gallery?.url
            },
            ...(config?.customGalleryPages || []).map(p => ({
                path: `/gallery/${p.slug}`,
                label: p.title.toUpperCase(),
                icon: p.icon || '🖼️',
                key: 'gallery',
                type: p.type,
                url: p.url
            })),
            { path: '/certificate', label: 'CERTIFICADO', icon: '🎓', key: 'certificate' },
            { path: '/thanks', label: 'AGRADECIMIENTO', icon: '🙏', key: 'thanks' },
            { path: '/interactiva', label: 'TRIVIA EN VIVO', icon: '🗳️', key: 'interactiva' },
            { path: '/profile', label: 'MI PERFIL', icon: '👤', key: 'profile' },
            ...(user?.role === 'admin' ? [{ path: '/scanner', label: 'Escanear', icon: '📷' }] : []),
        ].filter(item => {
            const design = config?.pageDesigns?.[item.key];
            return design ? design.enabled : true;
        });

    const isAgendaImageOnly = pageKey === 'agenda' && config?.pageDesigns?.agenda?.agendaImageOnly;
    // Precise check for gallery embed: check custom pages AND the main gallery page
    const currentGalleryPage = (config?.customGalleryPages || []).find(p => location.pathname.includes(p.slug));
    const isMainGalleryEmbed = pageKey === 'gallery' && location.pathname.includes('event-gallery') && config?.pageDesigns?.gallery?.type === 'embed';
    const isGalleryEmbed = (pageKey === 'gallery' && currentGalleryPage?.type === 'embed') || isMainGalleryEmbed;
    const hideHeaderFooter = isAgendaImageOnly || isGalleryEmbed;

    const bgImage = pageDesign.bgImage || config?.backgroundImage;
    const bgStyle = bgImage
        ? {
            backgroundImage: `url(${bgImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
        }
        : { backgroundColor: '#1c0042' };

    return (
        <div className="min-h-screen flex flex-col relative overflow-x-hidden transition-all duration-700 bg-[#1c0042]">
            {/* Background Layer */}
            {bgImage && (
                <div
                    className="fixed inset-0 z-0 pointer-events-none"
                    style={bgStyle}
                ></div>
            )}

            {!hideHeaderFooter && <Header
                showBrain={pageDesign.showBrain}
                title={pageDesign.title}
                subtitle={pageDesign.subtitle}
            />}

            <main className={`flex-grow container mx-auto relative z-10 
                ${(pageKey === 'interactiva-stats' || pageKey === 'gallery') ? 'max-w-none px-0' : 'max-w-4xl px-4 sm:px-6'} 
                ${hideHeaderFooter ? 'pb-0 p-0 min-h-screen pt-0' : 'pb-24 py-4'}`}>
                {children}
            </main>


            {/* Hamburger Button (Top Right) */}
            {user && (
                <div className="fixed top-4 right-4 sm:top-8 sm:right-8 z-[110] animate-fadeIn">
                    <button
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-2xl transition-all duration-500 border border-white/20 backdrop-blur-xl ${isMenuOpen ? 'bg-red-500/80 rotate-90 scale-110' : 'bg-white/10 hover:bg-white/20'
                            }`}
                    >
                        <span className="text-xl sm:text-2xl text-white">
                            {isMenuOpen ? '✕' : '☰'}
                        </span>
                    </button>
                </div>
            )}

            {/* Navigation Side Drawer */}
            <div className={`fixed inset-0 z-[100] transition-all duration-500 ${isMenuOpen ? 'visible' : 'invisible'
                }`}>
                {/* Backdrop Overlay */}
                <div
                    className={`absolute inset-0 bg-black/60 backdrop-blur-md transition-opacity duration-500 ${isMenuOpen ? 'opacity-100' : 'opacity-0'
                        }`}
                    onClick={() => setIsMenuOpen(false)}
                ></div>

                {/* Sidebar Panel */}
                <div className={`absolute top-0 right-0 w-[260px] sm:w-[300px] h-full bg-[#1c0042]/95 backdrop-blur-2xl border-l border-white/10 shadow-[-20px_0_50px_rgba(0,0,0,0.5)] transition-transform duration-500 transform ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'
                    }`}>
                    <div className="flex flex-col h-full p-8 pt-24">
                        <div className="flex-grow overflow-y-auto pr-2 custom-scrollbar">
                            <div className="space-y-3">
                                {navItems.map((item, index) => (
                                    <NavLink
                                        key={item.path}
                                        to={item.path}
                                        onClick={(e) => {
                                            if (item.type === 'redirect' && item.url) {
                                                e.preventDefault();
                                                window.open(item.url, '_blank');
                                                setIsMenuOpen(false);
                                                return;
                                            }
                                            setIsMenuOpen(false);
                                        }}
                                        style={{ transitionDelay: `${index * 30}ms` }}
                                        className={({ isActive }) =>
                                            `flex items-center p-4 rounded-2xl border border-white/5 transition-all duration-300 transform ${isMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
                                            } ${isActive && item.type !== 'redirect'
                                                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg'
                                                : 'bg-white/5 text-white/80 hover:bg-white/10 hover:text-white'
                                            }`
                                        }
                                    >
                                        <span className="text-xl mr-4">{item.icon}</span>
                                        <span className="text-xs font-black uppercase tracking-widest">{item.label}</span>
                                    </NavLink>
                                ))}
                            </div>
                        </div>

                        {/* Footer Section in Sidebar */}
                        <div className="mt-8 pt-8 border-t border-white/10 flex flex-col gap-4">
                            <div className="flex items-center p-4 rounded-2xl bg-white/5 border border-white/5">
                                <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center text-white font-black mr-3 shadow-lg">
                                    {user?.name?.[0].toUpperCase()}
                                </div>
                                <div className="overflow-hidden">
                                    <p className="text-xs font-black text-white truncate uppercase tracking-widest">{user?.name}</p>
                                    <p className="text-[9px] text-white/70 uppercase tracking-widest leading-none">Mi Perfil</p>
                                </div>
                            </div>

                            <button
                                onClick={handleLogout}
                                className="flex items-center justify-center gap-3 w-full py-4 rounded-2xl bg-red-500/10 border border-red-500/30 text-red-200 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-red-500/20 transition-all"
                            >
                                <span>🚪</span> Cerrar Sesión
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {!hideHeaderFooter && (
                <footer className="w-full p-10 flex justify-center items-center z-40 bg-gradient-to-t from-black/20 to-transparent">
                    <img
                        src="https://ftp.pcnpublicidad.com/neuroupdate/resources/Texto_Legal.png"
                        alt="Información Legal"
                        className="h-auto w-[80%] opacity-100"
                    />
                </footer>
            )}
        </div>
    );
};

export default Layout;
